import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, SlidersHorizontal, Utensils } from 'lucide-react';
import type { MenuCategory, MenuItem } from '@kss/api';
import { useTableContext } from './useTableContext';
import { useDineMenu } from './useDineMenu';
import { addLine, type DineRootState } from './dineSlice';
import CartSheet from './CartSheet';

/**
 * Main menu screen — mirrors the reference flow:
 *   - Search input + Filters chip across the top.
 *   - Category rail (horizontally-scrollable tiles).
 *   - Item list grouped by category; veg/non-veg badge, image,
 *     price, +Add button.
 *
 * Tap "+ Add" to drop a line in the cart. The bottom-sheet
 * <CartSheet/> auto-opens when the cart has any lines.
 */
export default function DineMenu() {
  const { table } = useTableContext();
  // useDineMenu now takes the QR token directly and hits the
  // /api/restaurant/dine/{qrToken}/menu anonymous endpoint — no auth
  // required, so the customer doesn't need a guest login to see the menu.
  const { status, categories, items, error } = useDineMenu(table?.qrToken);
  const dispatch = useDispatch();
  const lineCount = useSelector((s: DineRootState) => s.dine.lines.length);

  const [search, setSearch] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  // The reference UI shows category-grouped sections rather than a
  // single flat list — group items here.
  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const inSearch = (i: MenuItem): boolean =>
      !term || i.name.toLowerCase().includes(term) || (i.description ?? '').toLowerCase().includes(term);
    const byCat = new Map<string, MenuItem[]>();
    for (const it of items) {
      if (!inSearch(it)) continue;
      const k = it.menuCategoryId ?? '__none__';
      if (!byCat.has(k)) byCat.set(k, []);
      byCat.get(k)!.push(it);
    }
    return byCat;
  }, [items, search]);

  // If an "active" category is selected, only that section renders.
  const visibleCategories: MenuCategory[] = useMemo(() => {
    if (!activeCategoryId) return categories;
    return categories.filter((c) => c.menuCategoryId === activeCategoryId);
  }, [categories, activeCategoryId]);

  if (status === 'loading') {
    return <div className="px-4 py-10 text-center text-sm text-content-muted">Loading menu…</div>;
  }
  if (status === 'error') {
    // The most common failure: API returned 401 because the live
    // deploy requires auth on every endpoint and no `dine-guest`
    // user is provisioned. Surface a clearer hint for the operator
    // than the bare axios "Request failed with status code 401".
    const is401 = /401|unauthorized/i.test(error ?? '');
    return (
      <div className="px-6 py-10 text-center">
        <div className="mx-auto mb-3 grid h-16 w-16 place-items-center rounded-full bg-primary-light text-primary">
          <Utensils size={28} />
        </div>
        <h2 className="text-base font-semibold text-content-primary">
          {is401 ? 'Menu not yet available' : 'Could not load the menu'}
        </h2>
        <p className="mx-auto mt-1 max-w-xs text-sm text-content-muted">
          {is401
            ? 'Please ask the restaurant team to enable QR ordering for this tenant. Operator: see backend gap G39 / §32.'
            : (error ?? 'Try again in a moment.')}
        </p>
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* Search + filters row */}
      <div className="sticky top-0 z-[1] flex items-center gap-2 bg-surface-app px-3 pt-3 pb-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item"
            className="h-11 w-full rounded-lg border border-border bg-surface-card pl-9 pr-3 text-sm outline-none focus:border-primary"
          />
        </div>
        <button
          type="button"
          className="flex h-11 items-center gap-1.5 rounded-lg border border-border bg-surface-card px-3 text-sm text-content-secondary"
        >
          Filters <SlidersHorizontal size={14} />
        </button>
      </div>

      {/* Category rail */}
      <div className="flex gap-3 overflow-x-auto px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {categories.map((cat) => {
          const active = activeCategoryId === cat.menuCategoryId;
          return (
            <button
              key={cat.menuCategoryId}
              type="button"
              onClick={() =>
                setActiveCategoryId(active ? null : cat.menuCategoryId ?? null)
              }
              className={`flex w-24 shrink-0 flex-col items-center gap-1.5 rounded-xl border px-2 py-2 ${
                active
                  ? 'border-primary bg-surface-card'
                  : 'border-border bg-surface-card'
              }`}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-light">
                {cat.imageUrl ? (
                  <img src={cat.imageUrl} alt="" className="h-full w-full rounded-lg object-cover" />
                ) : (
                  <Utensils size={20} className="text-primary" />
                )}
              </div>
              <span
                className={`line-clamp-2 text-center text-[12px] leading-tight ${
                  active ? 'font-semibold text-primary underline underline-offset-4' : 'text-content-secondary'
                }`}
              >
                {cat.name}
              </span>
            </button>
          );
        })}
      </div>

      {/* Grouped item sections */}
      <div className="px-3">
        {visibleCategories.map((cat) => {
          const rows = grouped.get(cat.menuCategoryId ?? '') ?? [];
          if (rows.length === 0) return null;
          return (
            <section key={cat.menuCategoryId} className="mt-2">
              <header className="flex items-center justify-between py-2">
                <h2 className="text-[15px] font-semibold text-content-primary">
                  {cat.name} <span className="text-content-muted">({rows.length})</span>
                </h2>
              </header>
              <ul className="space-y-3">
                {rows.map((it) => (
                  <MenuItemRow
                    key={it.menuItemId}
                    item={it}
                    onAdd={() =>
                      dispatch(
                        addLine({
                          menuItemId: String(it.menuItemId),
                          name: it.name,
                          price: Number(it.price ?? 0),
                          imageUrl: it.imageUrl,
                          isVeg: it.isVeg,
                          taxRate: it.taxRate,
                        }),
                      )
                    }
                  />
                ))}
              </ul>
            </section>
          );
        })}

        {/* Items with no category (fallback bucket). */}
        {!activeCategoryId && (grouped.get('__none__')?.length ?? 0) > 0 && (
          <section className="mt-2">
            <header className="py-2">
              <h2 className="text-[15px] font-semibold text-content-primary">
                Other ({grouped.get('__none__')!.length})
              </h2>
            </header>
            <ul className="space-y-3">
              {grouped.get('__none__')!.map((it) => (
                <MenuItemRow
                  key={it.menuItemId}
                  item={it}
                  onAdd={() =>
                    dispatch(
                      addLine({
                        menuItemId: String(it.menuItemId),
                        name: it.name,
                        price: Number(it.price ?? 0),
                        imageUrl: it.imageUrl,
                        isVeg: it.isVeg,
                        taxRate: it.taxRate,
                      }),
                    )
                  }
                />
              ))}
            </ul>
          </section>
        )}

        {visibleCategories.length === 0 && items.length === 0 && (
          <div className="py-16 text-center text-sm text-content-muted">
            No items available right now.
          </div>
        )}
      </div>

      {lineCount > 0 && <CartSheet />}
    </div>
  );
}

function MenuItemRow({ item, onAdd }: { item: MenuItem; onAdd: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const description = item.description ?? '';
  const needsTruncate = description.length > 80;
  return (
    <li className="flex items-start gap-3 border-b border-border pb-3 last:border-0">
      <div className="flex-1">
        <div className="mb-1 flex items-center gap-1.5">
          <VegBadge isVeg={item.isVeg} />
        </div>
        <h3 className="text-[15px] font-semibold leading-snug text-content-primary">{item.name}</h3>
        <div className="mt-0.5 text-sm font-medium text-content-primary">
          ₹ {Number(item.price ?? 0).toFixed(2)}
        </div>
        {description && (
          <p className="mt-1 text-[13px] leading-snug text-content-muted">
            {expanded || !needsTruncate ? description : description.slice(0, 80) + '… '}
            {needsTruncate && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="font-medium text-primary"
              >
                {expanded ? 'Show Less' : 'Read More'}
              </button>
            )}
          </p>
        )}
      </div>
      <div className="relative w-[110px] shrink-0">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt="" className="h-[88px] w-[110px] rounded-lg object-cover" />
        ) : (
          <div className="flex h-[88px] w-[110px] items-center justify-center rounded-lg bg-surface-app text-content-muted">
            <Utensils size={24} />
          </div>
        )}
        <button
          type="button"
          onClick={onAdd}
          className="absolute inset-x-1 bottom-1 rounded-md border border-primary bg-surface-card py-1 text-[13px] font-semibold text-primary shadow-sm hover:bg-primary-light"
        >
          + Add
        </button>
      </div>
    </li>
  );
}

function VegBadge({ isVeg }: { isVeg?: boolean }) {
  // NOTE: these green (#16a34a) / red (#b91c1c) hex values are the
  // semantic Indian veg / non-veg food-safety mark — NOT theme chrome.
  // They are intentionally hard-coded and must stay fixed across themes.
  const color = isVeg === false ? '#b91c1c' : '#16a34a';
  return (
    <span
      aria-label={isVeg === false ? 'Non-veg' : 'Veg'}
      className="inline-flex h-4 w-4 items-center justify-center rounded-sm border-2"
      style={{ borderColor: color }}
    >
      <span className="block h-2 w-2 rounded-full" style={{ background: color }} />
    </span>
  );
}
